import { __assign } from 'tslib';
import React, { forwardRef, useRef, useState, useCallback } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { IconButton } from '@alfalab/core-components-icon-button/esm';
import { g as getDataTestId } from '../../warning-e5d5746a.js';
import { useFocus } from '@alfalab/hooks';
import { ChevronDownMIcon } from '@alfalab/icons-glyph/ChevronDownMIcon';
import { CrossMIcon } from '@alfalab/icons-glyph/CrossMIcon';
import { ButtonList } from '../button-list/component.js';

var commonStyles = {"component":"plate__component_1g51u","customBorder":"plate__customBorder_1g51u","foldable":"plate__foldable_1g51u","focused":"plate__focused_1g51u","inner":"plate__inner_1g51u","rounded":"plate__rounded_1g51u","noBorder":"plate__noBorder_1g51u","shadow":"plate__shadow_1g51u","common":"plate__common_1g51u","negative":"plate__negative_1g51u","positive":"plate__positive_1g51u","attention":"plate__attention_1g51u","custom":"plate__custom_1g51u","rect":"plate__rect_1g51u","isHidden":"plate__isHidden_1g51u","leftAddons":"plate__leftAddons_1g51u","rightAddons":"plate__rightAddons_1g51u","subAddons":"plate__subAddons_1g51u","rowReverse":"plate__rowReverse_1g51u","bold":"plate__bold_1g51u","light":"plate__light_1g51u","content":"plate__content_1g51u","isFolded":"plate__isFolded_1g51u","contentContainer":"plate__contentContainer_1g51u","withoutTitle":"plate__withoutTitle_1g51u","limitWidth":"plate__limitWidth_1g51u","description":"plate__description_1g51u","closer":"plate__closer_1g51u","folder":"plate__folder_1g51u","containerButton":"plate__containerButton_1g51u","button":"plate__button_1g51u","rowLimit1":"plate__rowLimit1_1g51u","rowLimit2":"plate__rowLimit2_1g51u","rowLimit3":"plate__rowLimit3_1g51u"};
require('./index.css')

/* eslint-disable complexity */
var BasePlate = forwardRef(function (_a, ref) {
    var _b, _c, _d, _e, _f;
    var hasCloser = _a.hasCloser, _g = _a.foldable, foldableProp = _g === void 0 ? false : _g, foldedProp = _a.folded, _h = _a.defaultFolded, defaultFolded = _h === void 0 ? true : _h, _j = _a.rounded, rounded = _j === void 0 ? true : _j, _k = _a.limitContentWidth, limitContentWidth = _k === void 0 ? true : _k, leftAddons = _a.leftAddons, subAddons = _a.subAddons, children = _a.children, buttons = _a.buttons, title = _a.title, _l = _a.titleView, titleView = _l === void 0 ? 'bold' : _l, _m = _a.view, view = _m === void 0 ? 'common' : _m, _o = _a.border, border = _o === void 0 ? view !== 'custom' : _o, _p = _a.shadow, shadow = _p === void 0 ? view === 'custom' : _p, className = _a.className, buttonsClassName = _a.buttonsClassName, contentClassName = _a.contentClassName, subAddonsClassName = _a.subAddonsClassName, dataTestId = _a.dataTestId, onClick = _a.onClick, onClose = _a.onClose, onToggle = _a.onToggle, rowLimit = _a.rowLimit, _q = _a.styles, styles = _q === void 0 ? {} : _q, background = _a.background, borderColor = _a.borderColor;
    var plateRef = useRef(null);
    var contentRef = useRef(null);
    var subAddonsRef = useRef(null);
    var focused = useFocus(plateRef, 'keyboard')[0];
    var _r = useState(false), isHidden = _r[0], setIsHidden = _r[1];
    var _s = useState(defaultFolded), foldedState = _s[0], setFoldedState = _s[1];
    var uncontrolled = foldedProp === undefined;
    var foldable = !!title && !!children && foldableProp;
    var folded = uncontrolled ? foldedState : foldedProp;
    var hasButtons = !!buttons && typeof buttons !== 'boolean';
    var hasContent = children || hasButtons;
    var hasSubAddons = !!subAddons && typeof subAddons !== 'boolean';
    var hasAnyAddons = leftAddons || subAddons || foldable || hasCloser;
    var rowLimitStyles = rowLimit && commonStyles["rowLimit".concat(rowLimit)];
    var isCustomView = view === 'custom';
    var handleClick = useCallback(function (event) {
        var clickSimilarKeys = ['Enter', ' '].includes(event.key);
        if (event.type !== 'click' && !clickSimilarKeys) {
            return;
        }
        var target = event.target;
        var eventInsideComponent = plateRef.current && plateRef.current.contains(target);
        var eventInsideContent = contentRef.current && contentRef.current.contains(target);
        var eventInsideSubAddons = subAddonsRef.current && subAddonsRef.current.contains(target);
        var shouldChangeIsFolded = eventInsideComponent && !eventInsideContent && !eventInsideSubAddons;
        if (foldable && shouldChangeIsFolded) {
            if (uncontrolled) {
                setFoldedState(!foldedState);
            }
            if (onToggle) {
                onToggle(event, { folded: !(uncontrolled ? foldedState : foldedProp) });
            }
        }
        if (onClick) {
            onClick(event);
        }
    }, [foldable, onClick, uncontrolled, onToggle, foldedState, foldedProp]);
    var handleClose = useCallback(function (event) {
        setIsHidden(true);
        if (onClose) {
            onClose(event);
        }
    }, [onClose]);
    var setCustomViewColors = function () {
        if (isCustomView) {
            return {
                background: background,
                borderColor: borderColor,
            };
        }
        return {};
    };
    var setCustomViewRectangleBorderColor = function () {
        if (isCustomView && border && !rounded && borderColor) {
            return {
                boxShadow: "2px 0 0 0 ".concat(borderColor, " inset"),
            };
        }
        return {};
    };
    return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    React.createElement("div", { className: cn(commonStyles.component, commonStyles[view], (_b = {},
            _b[commonStyles.foldable] = foldable,
            _b[commonStyles.focused] = focused,
            _b[commonStyles.isHidden] = hasCloser && isHidden,
            _b[commonStyles.isFolded] = foldable && folded,
            _b[commonStyles.rounded] = rounded,
            _b[styles.rounded] = rounded,
            _b[commonStyles.rect] = !rounded,
            _b[commonStyles.noBorder] = !border,
            _b[commonStyles.shadow] = shadow,
            _b[styles.shadow] = shadow,
            _b[commonStyles.customBorder] = border && !rounded && isCustomView,
            _b), className), style: __assign(__assign({}, setCustomViewColors()), setCustomViewRectangleBorderColor()), onClick: handleClick, onKeyDown: handleClick, role: 'alert', ref: mergeRefs([plateRef, ref]), 
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
        tabIndex: foldable ? 0 : -1, "data-test-id": dataTestId },
        React.createElement("div", { className: commonStyles.inner },
            leftAddons && React.createElement("div", { className: commonStyles.leftAddons }, leftAddons),
            React.createElement("div", { className: cn(commonStyles.contentContainer, contentClassName, (_c = {},
                    _c[commonStyles.withoutTitle] = !title && hasAnyAddons,
                    _c[commonStyles.limitWidth] = limitContentWidth,
                    _c)) },
                title && (React.createElement("div", { "data-test-id": getDataTestId(dataTestId, 'title'), className: cn(commonStyles[titleView], (_d = {},
                        _d[styles[titleView]] = Boolean(styles[titleView]),
                        _d)) }, title)),
                hasContent && (React.createElement("div", { ref: contentRef, className: cn(commonStyles.content, (_e = {},
                        _e[commonStyles.isFolded] = foldable && folded,
                        _e)) },
                    children && (React.createElement("div", { className: cn(commonStyles.description, styles.description, rowLimitStyles) }, children)),
                    hasButtons && (React.createElement("div", { className: styles.footer },
                        React.createElement(ButtonList, { buttons: buttons, containerClassName: cn(commonStyles.containerButton, buttonsClassName), buttonClassName: cn(commonStyles.button, buttonsClassName) })))))),
            hasSubAddons && (React.createElement("div", { ref: subAddonsRef, className: commonStyles.subAddons },
                React.createElement(ButtonList, { buttons: subAddons, containerClassName: cn(subAddonsClassName, commonStyles.rowReverse), buttonClassName: commonStyles.button }))),
            foldable && (React.createElement("div", { className: commonStyles.rightAddons },
                React.createElement("div", { className: cn(commonStyles.folder, (_f = {},
                        _f[commonStyles.isFolded] = folded,
                        _f)) },
                    React.createElement(ChevronDownMIcon, null)))),
            hasCloser && !foldable && (React.createElement("div", { className: commonStyles.rightAddons },
                React.createElement(IconButton, { className: commonStyles.closer, "aria-label": '\u0437\u0430\u043A\u0440\u044B\u0442\u044C', icon: CrossMIcon, size: 'xxs', onClick: handleClose }))))));
});

export { BasePlate };
